import React from 'react';
import { PlusIcon } from 'lucide-react';
import Image from 'next/image';
import { TrashIcon } from 'lucide-react';
import clsx from 'clsx';
import { ReloadIcon } from '@radix-ui/react-icons';

type InputFileType = {
  type?: 'file' | 'image' | 'video';
  name: string;
  ratio?: string;
  loading?: boolean;
  onChange: (
    files:
      | {
          thumbnail: string;
          file: unknown;
        }
      | undefined
  ) => void;
};

const configByType: { [key: string]: { accept: string } } = {
  image: {
    accept: 'image/png, image/jpeg, image/jpg, image/webp, image/avif',
  },
  video: {
    accept: 'video/mp4, video/mov',
  },
};

const generateVideoThumbnail = (file: File) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const video = document.createElement('video');

    // this is important
    video.autoplay = true;
    video.muted = true;
    video.src = URL.createObjectURL(file);

    video.onloadeddata = () => {
      const ctx = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx?.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
      video.pause();
      return resolve(canvas.toDataURL('image/png'));
    };
  });
};

export default function InputSingleFile({
  type = 'image',
  name,
  ratio,
  loading,
  onChange,
}: InputFileType) {
  const config = configByType[type];

  const [fileData, setFileData] = React.useState<{
    thumbnail: string;
    file: unknown;
  }>();

  const getThubmnail = async (file: File) => {
    if (type === 'image') {
      return URL.createObjectURL(file);
    }
    return generateVideoThumbnail(file);
  };

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = (e.target.files || [])[0];
    if (file) {
      const thumbnail = (await getThubmnail(file)) as string;
      const tempFiles = {
        file,
        thumbnail,
      };

      setFileData(tempFiles);
      onChange(tempFiles);
    }
  };

  const onDelete = () => {
    setFileData(undefined);
    onChange(undefined);
  };

  return (
    <div>
      {fileData?.thumbnail ? (
        <div className='group relative w-full'>
          <label htmlFor={`inputFile_${name}`}>
            <Image
              className={clsx(
                'w-full cursor-pointer rounded-lg border border-dashed border-[#AEABAB] object-contain duration-300 hover:bg-stone-100',
                ratio || 'aspect-[16/9]'
              )}
              src={fileData.thumbnail}
              alt='Images'
              width={268}
              height={150}
            />
            {loading && (
              <div className='flex items-center justify-center gap-2'>
                <ReloadIcon className='h-4 w-4 animate-spin' />
                <span>Uploading...</span>
              </div>
            )}
          </label>
          <button
            className='absolute right-1 top-1 rounded border bg-white p-1 opacity-0 duration-100 hover:bg-stone-100 group-hover:opacity-100'
            onClick={() => onDelete()}
          >
            <TrashIcon className='h-4 w-4 text-red-600' />
          </button>
        </div>
      ) : (
        <label htmlFor={`inputFile_${name}`}>
          <div
            className={clsx(
              'flex w-full cursor-pointer items-center justify-center gap-x-2 rounded-lg border border-dashed border-[#AEABAB] duration-300 hover:bg-stone-100',
              ratio || 'aspect-[16/9]'
            )}
          >
            <PlusIcon className='h-4 w-4 text-[#45464E]' />
            <span>{type === 'image' ? 'Select Image' : 'Select Video'}</span>
          </div>
        </label>
      )}
      <input
        id={`inputFile_${name}`}
        type='file'
        className='hidden'
        multiple
        accept={config.accept}
        onChange={onUpload}
        disabled={loading}
      />
    </div>
  );
}
